import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Boxes, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { AttentionStrip, EmptyState } from '../../components/ui';
import { inventoryAPI } from '../../services/api';
import AssetTabs from './AssetTabs';

export default function InventoryList() {
  const [searchParams] = useSearchParams();
  const lowStockOnly = searchParams.get('low_stock') === 'true';

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [page, setPage] = useState(1);
  const limit = 10;

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.list({
        search: search || undefined,
        status: status || undefined,
        low_stock: lowStockOnly ? 'true' : undefined,
        page,
        limit,
      });
      setList(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [search, status, page, lowStockOnly]);

  const totalPages = Math.ceil(total / limit) || 1;
  const lowStockInView = list.filter((item) => item.is_low_stock).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Boxes className="h-7 w-7 text-ga-primary" />
              Inventory
            </h1>
            <p className="text-ga-text-muted mt-1">Track consumables — balls, bibs, cones, kits</p>
          </div>
          <Link to="/dashboard/assets/inventory/new" className="btn-primary-solid inline-flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Item
          </Link>
        </div>

        <AssetTabs active="inventory" />

        {lowStockOnly && (
          <AttentionStrip
            tone="info"
            title="Showing low-stock items only"
            message="Items at or below their reorder threshold."
          />
        )}

        {!lowStockOnly && lowStockInView > 0 && (
          <AttentionStrip
            icon={Boxes}
            tone="danger"
            title={`${lowStockInView} item${lowStockInView === 1 ? '' : 's'} below reorder threshold on this page`}
            message="Restock soon to avoid running out."
            action={
              <Link to="/dashboard/assets/inventory?low_stock=true" className="ga-btn-primary px-3 py-1.5 text-sm">
                View all low stock
              </Link>
            }
          />
        )}

        <div className="ga-card rounded-xl shadow-sm border border-ga-border-subtle p-4">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ga-text-subtle" />
              <input
                type="text"
                placeholder="Search by name, make, colour, category..."
                aria-label="Search inventory"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="ga-input w-full pl-10 pr-4 py-2"
              />
            </div>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              aria-label="Filter by status"
              className="ga-input w-full sm:w-44 px-4 py-2"
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>

          {loading ? (
            <div className="py-12 text-center text-ga-text-subtle">Loading...</div>
          ) : list.length === 0 ? (
            <EmptyState
              card
              icon={Boxes}
              title="No inventory items found"
              description="Add consumables to track quantity, restock, and issue to staff."
              action={
                <Link to="/dashboard/assets/inventory/new" className="btn-primary-solid inline-flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Add Item
                </Link>
              }
            />
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {list.map((item) => (
                  <Link
                    key={item.id}
                    to={`/dashboard/assets/inventory/${item.id}`}
                    className="block ga-card p-4 border border-ga-border-subtle hover:border-ga-primary/40"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-semibold text-ga-text-primary">{item.item_name}</p>
                        <p className="text-sm text-ga-text-muted">{item.category || 'Uncategorized'}</p>
                        {(item.make || item.colour) && (
                          <p className="text-xs text-ga-text-subtle mt-0.5">
                            {[item.make, item.colour, item.size].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                      {item.is_low_stock && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-ga-danger/15 text-ga-danger">Low</span>
                      )}
                    </div>
                    <p className="mt-2 text-lg font-bold text-ga-text-primary">
                      {Number(item.quantity_on_hand).toLocaleString()} <span className="text-sm font-normal text-ga-text-muted">{item.unit}</span>
                    </p>
                  </Link>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ga-border-subtle">
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Item</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Make</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Colour</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Category</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">On hand</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Reorder at</th>
                      <th className="text-left py-3 px-2 font-semibold text-ga-text-muted">Location</th>
                      <th className="text-right py-3 px-2 font-semibold text-ga-text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item) => (
                      <tr key={item.id} className="border-b border-ga-border-subtle/50 hover:bg-ga-bg-surface-subtle/50">
                        <td className="py-3 px-2">
                          <Link to={`/dashboard/assets/inventory/${item.id}`} className="text-ga-primary hover:underline font-medium">
                            {item.item_name}
                          </Link>
                          {item.is_low_stock && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-ga-danger/15 text-ga-danger">Low stock</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-ga-text-muted">{item.make || '-'}</td>
                        <td className="py-3 px-2 text-ga-text-muted">{item.colour || '-'}</td>
                        <td className="py-3 px-2 text-ga-text-muted">{item.category || '-'}</td>
                        <td className="py-3 px-2 text-ga-text-primary font-medium">
                          {Number(item.quantity_on_hand).toLocaleString()} {item.unit}
                        </td>
                        <td className="py-3 px-2 text-ga-text-muted">
                          {item.reorder_threshold != null ? `${Number(item.reorder_threshold).toLocaleString()} ${item.unit}` : '-'}
                        </td>
                        <td className="py-3 px-2 text-ga-text-muted">{item.location || '-'}</td>
                        <td className="py-3 px-2 text-right">
                          <Link to={`/dashboard/assets/inventory/${item.id}`} className="text-ga-primary hover:underline text-sm font-medium">
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-ga-border-subtle">
                  <span className="text-sm text-ga-text-muted">Page {page} of {totalPages} ({total} total)</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      aria-label="Previous page"
                      className="p-2 rounded-lg border border-ga-border-subtle disabled:opacity-50 hover:bg-ga-bg-surface-subtle text-ga-text-primary"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      aria-label="Next page"
                      className="p-2 rounded-lg border border-ga-border-subtle disabled:opacity-50 hover:bg-ga-bg-surface-subtle text-ga-text-primary"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
